/**
 * ImportWizardShell — the chrome both bulk-import wizards wear.
 *
 * The users wizard and the clients wizard were the same dialog written twice:
 * the same headless-ui `Transition`/`Dialog` scaffold, the same header row, two
 * slightly different hand-rolled step-chip rails (one with `w-4` connectors, one
 * with `w-6`), the same hand-built danger banner, and a footer whose height
 * changed with its contents — so the panel visibly grew and shrank as the user
 * moved from Upload (one button) to Parsing (none) to Preview (two).
 *
 * One shell now, on the platform pieces: the shared `WizardStepRail` for steps,
 * `Alert` for the error row, and a footer with a pinned minimum height so the
 * dialog never jumps between steps. Both wizards run at the same width.
 *
 * The shell owns chrome only — each wizard still owns its own steps, state and
 * submit flow.
 */

import React, { Fragment } from 'react';
import clsx from 'clsx';
import { Dialog, Transition } from '@headlessui/react';
import { useTranslation } from 'react-i18next';
import {
  PiXLight,
  PiWarningCircleLight,
  PiCheckCircleLight,
  PiPencilLight,
  PiTrashLight,
} from 'react-icons/pi';

import Alert from '../shared/TailwindComponents/Alert';
import Spinner from '../shared/TailwindComponents/Spinner';
import BrandLoader from '../shared/brand/BrandLoader';
import GlassCard from '../shared/brand/GlassCard';
import { KpiTile, Well } from '../shared/brand/Field';
import type { FieldTone } from '../shared/brand/Field';
import { FOCUS_RING_ONLY } from '../shared/TailwindComponents/FieldShell';
import WizardStepRail, {
  WizardStepRailItem,
} from '../shared/FormComponents/WizardStepRail';

/** Both import dialogs run at this width — they show the same shape of table. */
export const IMPORT_WIZARD_MAX_WIDTH = 'max-w-5xl';

export interface ImportWizardShellProps<K extends string> {
  isOpen: boolean;
  /** Header glyph, sized by the shell. */
  icon: React.ReactNode;
  /** Already-translated dialog title. */
  title: string;
  steps: WizardStepRailItem<K>[];
  currentStep: K;
  /** Error text for the banner. Falsy → no banner. */
  error?: string | null;
  onDismissError?: () => void;
  onClose: () => void;
  /** Left-hand footer slot — a running count, a caveat. */
  footerLeading?: React.ReactNode;
  /** Right-hand footer slot — the buttons. */
  footerActions?: React.ReactNode;
  children: React.ReactNode;
}

function ImportWizardShell<K extends string>({
  isOpen,
  icon,
  title,
  steps,
  currentStep,
  error,
  onDismissError,
  onClose,
  footerLeading,
  footerActions,
  children,
}: Readonly<ImportWizardShellProps<K>>) {
  const { t } = useTranslation();

  return (
    <Transition appear show={isOpen} as={Fragment}>
      {/* Dismissal runs through the header/footer buttons only: a backdrop
          click mid-import would abandon a job the backend is still running. */}
      <Dialog as="div" className="relative z-50" onClose={() => {}}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-ink/20 backdrop-blur-[2px]" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-slide duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={clsx(
                  'w-full transform overflow-hidden rounded-2xl glass shadow-sm transition-all',
                  IMPORT_WIZARD_MAX_WIDTH
                )}
              >
                {/* Header */}
                <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-hairline">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-fg-muted [&>svg]:h-5 [&>svg]:w-5">{icon}</span>
                    <Dialog.Title className="text-lg text-fg-strong truncate">
                      {title}
                    </Dialog.Title>
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label={t('Close')}
                    className={clsx(
                      'p-1 rounded-lg text-fg-muted hover:bg-surface-sunken hover:text-fg-body transition-colors',
                      FOCUS_RING_ONLY
                    )}
                  >
                    <PiXLight className="w-5 h-5" />
                  </button>
                </div>

                {/* Step rail — the same rail the matter and drafting wizards use. */}
                <div className="border-b border-hairline-soft">
                  <WizardStepRail
                    steps={steps}
                    currentStep={currentStep}
                    className="px-6 py-3"
                    maxWidth="max-w-none"
                  />
                </div>

                {/* Body — fixed envelope so the panel keeps one size across steps. */}
                <div className="px-6 py-6 min-h-[400px] max-h-[60vh] overflow-y-auto custom-scrollbar-main">
                  {error && (
                    <div className="mb-4">
                      <Alert
                        type="error"
                        message={error}
                        onClose={onDismissError}
                      />
                    </div>
                  )}
                  {children}
                </div>

                {/* Footer — `min-h` pins the row so a step with no buttons is the
                    same height as one with two, and the dialog never jumps. */}
                <div className="glass-bar border-t border-hairline px-6 py-4">
                  <div className="flex min-h-8 items-center justify-between gap-4">
                    <div className="min-w-0">{footerLeading}</div>
                    <div className="flex flex-shrink-0 items-center gap-2">
                      {footerActions}
                    </div>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

/* ── Shared step bodies ─────────────────────────────────────────────── */

export interface ImportProcessingStateProps {
  /** Already-translated headline. */
  title: string;
  /** Already-translated secondary line. */
  detail?: string;
  /**
   * `true` only for the AI parse/classify pass — the one moment in these
   * wizards that is genuinely an AI process, and so the one place the logo
   * loader is allowed (see shared/LOADERS.md). Creating rows and sending
   * invitations is ordinary work and gets the plain Spinner.
   */
  ai?: boolean;
}

/** The centred "working…" body shared by the parsing and confirm steps. */
export const ImportProcessingState: React.FC<ImportProcessingStateProps> = ({
  title,
  detail,
  ai = false,
}) => (
  <div className="flex flex-col items-center justify-center gap-4 py-12">
    {ai ? <BrandLoader size="lg" /> : <Spinner size="lg" />}
    <h3 className="text-sm font-medium text-fg-body">{title}</h3>
    {detail && <p className="text-sm text-fg-muted text-center">{detail}</p>}
  </div>
);

export interface ImportResultHeaderProps {
  succeeded: boolean;
  /** Already-translated headline. */
  title: string;
  /** Already-translated line under it. */
  detail: string;
}

/** The outcome banner at the top of the results step. */
export const ImportResultHeader: React.FC<ImportResultHeaderProps> = ({
  succeeded,
  title,
  detail,
}) => (
  <Well className="flex items-center gap-4">
    {succeeded ? (
      <PiCheckCircleLight className="w-10 h-10 flex-shrink-0 text-success-500" />
    ) : (
      <PiWarningCircleLight className="w-10 h-10 flex-shrink-0 text-danger-500" />
    )}
    <div className="min-w-0">
      <h3 className="text-base font-medium text-fg-strong">{title}</h3>
      <p className="text-sm text-fg-muted">{detail}</p>
    </div>
  </Well>
);

export interface ImportStat {
  value: number;
  /** Already-translated label. */
  label: string;
  tone: FieldTone;
  hint?: string;
}

/**
 * Static, because Tailwind's JIT scans source text — an interpolated
 * `grid-cols-${n}` never reaches the stylesheet. Both wizards show 2–4 stats.
 */
const STAT_COLS: Record<number, string> = {
  1: 'grid-cols-1',
  2: 'grid-cols-2',
  3: 'grid-cols-3',
  4: 'grid-cols-2 sm:grid-cols-4',
};

/**
 * The results counters, as the platform's KPI strip — a single glass card cut
 * by hairlines, not three separately-tinted boxes.
 */
export const ImportStatStrip: React.FC<{ stats: ImportStat[] }> = ({ stats }) => (
  <GlassCard padding="none">
    <div className={clsx('grid divide-x divide-hairline-soft', STAT_COLS[stats.length] || STAT_COLS[3])}>
      {stats.map((s) => (
        <KpiTile
          key={s.label}
          value={s.value}
          label={s.label}
          hint={s.hint}
          tone={s.tone}
        />
      ))}
    </div>
  </GlassCard>
);

export interface ImportFailureListProps {
  /** Already-translated heading. */
  title: string;
  /** Already-translated one-line description per failure. */
  items: string[];
  tone?: 'danger' | 'warning';
  /** Rendered under the list when the source list was truncated. */
  overflowNote?: string;
}

/** The "these rows didn't make it" list shared by both results steps. */
export const ImportFailureList: React.FC<ImportFailureListProps> = ({
  title,
  items,
  tone = 'danger',
  overflowNote,
}) => (
  <div>
    <h4 className="text-sm font-medium text-fg-strong mb-2">{title}</h4>
    <div className="space-y-1 max-h-40 overflow-y-auto custom-scrollbar-main">
      {items.map((text, idx) => (
        <Well key={idx} className="flex items-center gap-2 !p-2">
          <PiWarningCircleLight
            className={clsx(
              'w-4 h-4 flex-shrink-0',
              tone === 'danger' ? 'text-danger-500' : 'text-warning-500'
            )}
          />
          <span className="text-xs text-fg-muted">{text}</span>
        </Well>
      ))}
      {overflowNote && <p className="text-xs text-fg-muted px-2 pt-1">{overflowNote}</p>}
    </div>
  </div>
);

/* ── Shared preview-table pieces ────────────────────────────────────── */

/** The preview table's header cell — one type scale for both wizards. */
export const IMPORT_TH_CLASS =
  'px-3 py-2 text-left text-xs font-medium text-fg-muted uppercase tracking-wide';

/** Row tint by state. Kept here so both preview tables read identically. */
export function importRowClass(opts: {
  duplicate?: boolean;
  blocked?: boolean;
  selected?: boolean;
}): string {
  const { duplicate, blocked, selected } = opts;
  return clsx(
    'transition-colors hover:bg-surface-sunken/70',
    blocked
      ? 'bg-danger-50/50 dark:bg-danger-900/10'
      : duplicate
        ? 'bg-warning-50/50 dark:bg-warning-900/10'
        : selected
          ? 'bg-transparent'
          : 'bg-surface-sunken/40 opacity-60'
  );
}

/** The select-all / select-none pair above both preview tables. */
export const ImportSelectionToggles: React.FC<{
  onSelectAll: () => void;
  onSelectNone: () => void;
}> = ({ onSelectAll, onSelectNone }) => {
  const { t } = useTranslation();
  const linkClass = clsx('rounded text-xs hover:underline', FOCUS_RING_ONLY);
  return (
    <div className="flex flex-shrink-0 items-center gap-2">
      <button type="button" onClick={onSelectAll} className={clsx(linkClass, 'text-fg-body')}>
        {t('Select All')}
      </button>
      <span aria-hidden="true" className="text-fg-muted/50">|</span>
      <button type="button" onClick={onSelectNone} className={clsx(linkClass, 'text-fg-muted')}>
        {t('Deselect All')}
      </button>
    </div>
  );
};

/** The small icon button pair (edit / remove) at the end of a preview row. */
export const ImportRowActions: React.FC<{
  onEdit: () => void;
  onRemove: () => void;
  editing: boolean;
}> = ({ onEdit, onRemove, editing }) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-1">
      <button
        type="button"
        onClick={onEdit}
        title={editing ? t('Done') : t('Edit')}
        aria-label={editing ? t('Done') : t('Edit')}
        className={clsx(
          'p-1 rounded text-fg-muted hover:bg-surface-sunken hover:text-fg-body transition-colors',
          FOCUS_RING_ONLY
        )}
      >
        <PiPencilLight className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={onRemove}
        title={t('Remove')}
        aria-label={t('Remove')}
        className={clsx(
          'p-1 rounded text-fg-muted hover:bg-danger-50 hover:text-danger-500 dark:hover:bg-danger-900/20 transition-colors',
          FOCUS_RING_ONLY
        )}
      >
        <PiTrashLight className="w-4 h-4" />
      </button>
    </div>
  );
};

export default ImportWizardShell;
